import { Drawer } from '@seeneco-ui/core';
import { BASE_DATE_FORMAT } from '@seeneco-ui/core/constants';
import * as moment from 'moment';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Dispatch, bindActionCreators } from 'redux';

import { LeadCheckCode } from '@api/leads/model';
import { SendSmsOptions } from '@core/api/atr-framework/model';
import { IAppState } from '@core/store';
import { formatLegalEntityName } from '@core/utils';
import { mainLeadsActionsPack } from '@leads/actions';
import { mainLeadsSelectorsPack } from '@leads/selectors';
import { UpdateLeadForm as XUpdateLeadForm, UpdateLeadFormProps as XUpdateLeadFormProps } from './update-lead.view';

export type UpdateLeadFormProps = {
	addLead?: typeof mainLeadsActionsPack.actions.addLead;
	fetchCompanyCardByTaxCode?: typeof mainLeadsActionsPack.actions.fetchCompanyCardByTaxCode;
	newLeadSmsText?: string;
	sendSmsNotification?: typeof mainLeadsActionsPack.actions.sendSmsNotification;
	updateLead?: typeof mainLeadsActionsPack.actions.updateLead;
} & Pick<XUpdateLeadFormProps, 'appearance' | 'lead'>;

const UpdateLeadFormBase: React.SFC<UpdateLeadFormProps> = props => {
	const {
		addLead,
		appearance,
		fetchCompanyCardByTaxCode,
		lead,
		newLeadSmsText,
		sendSmsNotification,
		updateLead,
		...rest
	} = props;
	const [errorCode, setErrorCode] = useState<LeadCheckCode>(null);
	const [isFetchingCompanyCard, setIsFetchingCompanyCard] = useState<boolean>(false);
	const [formObject, setFormObject] = useState<Lead>({ ...lead });

	const closeForm = () => Drawer.default.get().close();

	const handleChange = (changedFormObject: Lead) => {
		if (changedFormObject?.ID === formObject?.ID) {
			setFormObject(changedFormObject);
		}
	};

	const handleSearchCompanyCard = () => {
		if (!formObject.CompanyTaxCode) {
			setFormObject({
				...formObject,
				CompanyName: '',
			});
			return;
		}

		setIsFetchingCompanyCard(true);
		fetchCompanyCardByTaxCode(formObject.CompanyTaxCode, companyCard => {
			setFormObject({
				...formObject,
				CompanyName: formatLegalEntityName(companyCard?.LegalName),
			});
			setIsFetchingCompanyCard(false);
		});
	};

	const handleSubmit = (lead: Lead) => {
		if (appearance === 'add') {
			if (!lead.CreationDate) {
				lead.CreationDate = moment().format(BASE_DATE_FORMAT);
			}

			addLead(lead, code => {
				if (code === LeadCheckCode.HAS_SAME_EMAIL) {
					setErrorCode(code);
				} else {
					if (Boolean(newLeadSmsText)) {
						const options: SendSmsOptions = {
							Message: newLeadSmsText,
							PhoneNumber: lead.ContactPhone,
						};

						sendSmsNotification(options);
					}

					closeForm();
				}
			});
		} else if (appearance === 'edit') {
			updateLead(lead);
			closeForm();
		}
	};

	return (
		<XUpdateLeadForm
			appearance={appearance}
			errorCode={errorCode}
			isFetching={isFetchingCompanyCard}
			lead={formObject}
			onChange={handleChange}
			onSearchCompanyCard={handleSearchCompanyCard}
			onSubmit={handleSubmit}
			{...rest}
		/>
	);
};

UpdateLeadFormBase.defaultProps = {};
UpdateLeadFormBase.displayName = 'UpdateLeadForm';

function mapStateToProps(state: IAppState): Partial<UpdateLeadFormProps> {
	return {
		newLeadSmsText: mainLeadsSelectorsPack.selectAsyncNewLeadSmsText.selectItem(state),
	};
}

function mapDispatchToProps(dispatch: Dispatch) {
	return bindActionCreators(
		{
			addLead: mainLeadsActionsPack.actions.addLead,
			fetchCompanyCardByTaxCode: mainLeadsActionsPack.actions.fetchCompanyCardByTaxCode,
			sendSmsNotification: mainLeadsActionsPack.actions.sendSmsNotification,
			updateLead: mainLeadsActionsPack.actions.updateLead,
		},
		dispatch,
	);
}

const UpdateLeadForm = connect<any, any, UpdateLeadFormProps>(mapStateToProps, mapDispatchToProps)(UpdateLeadFormBase);

type OpenUpdateFormDrawerOptions = {} & Pick<XUpdateLeadFormProps, 'appearance' | 'lead'>;

function openUpdateLeadFormDrawer(options: OpenUpdateFormDrawerOptions) {
	const { appearance, lead } = options;

	Drawer.default.get().open({
		content: (
			<Drawer.default.Content>
				<UpdateLeadForm appearance={appearance} lead={lead} />
			</Drawer.default.Content>
		),
		footer: null,
		header: <Drawer.default.Title>{appearance === 'add' ? 'Добавить лид' : 'Изменить лид'}</Drawer.default.Title>,
	});
}

export { openUpdateLeadFormDrawer };
export default UpdateLeadForm;
